import React, { useContext, useState } from "react";

import { useMutation, useQuery } from "@apollo/client";
import {
  Box,
  Grid,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { observer } from "mobx-react-lite";
import { useParams } from "react-router-dom";
import { makeStyles } from "tss-react/mui";

import { NCIcon } from "@/components/Icons/Icon";
import { graphql } from "@/gql";
import {
  PhysicalActivityLevel2,
  TargetPhysicalActivityLevel,
} from "@/gql/graphql";
import useDebouncedEffect from "@/hooks/useDebouncedEffect";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import useNumberInputState from "@/hooks/useNumberInputState";
import useUpdateOnChange from "@/hooks/useUpdateOnChange";
import { colors } from "@/theme/overrides/colors";
import { formatISODate } from "@/utils/datetime";
import { displayError } from "@/utils/errors";
import { apply } from "@/utils/functions";
import { round } from "@/utils/number";
import { humanize } from "@/utils/string";

import MealPlanFormStore from "../store/MealPlanFormStore";

enum PhysicalActivity {
  Sedentary = "SEDENTARY",
  LightActive = "LIGHT_ACTIVE",
  ModeratelyActive = "MODERATELY_ACTIVE",
  VeryActive = "VERY_ACTIVE",
  ExtremeActive = "EXTREME_ACTIVE",
}

const useStyles = makeStyles()((theme) => ({
  bold: {
    fontWeight: "bold",
  },
  header: {
    backgroundColor: colors.light,
  },
  sectionHeader: {
    marginTop: theme.spacing(3),
  },
  row: {
    minHeight: 88,
    borderBottom: "1px solid #F5F7FA",
  },
}));

const clientInformationTableQuery = graphql(`
  query ClientInformationTable($clientId: ID!) {
    client(id: $clientId) {
      id
      profile {
        id
        gender
        ageInYears
      }
      latestHeightCm {
        id
        value
      }
      latestWeightKg {
        id
        value
      }
      lifestyle {
        id
        physicalActivityLevel2
      }
      journalGoal {
        id
        targetWeightKg
        targetPhysicalActivityLevel
      }
      bmi
      bmiCategory
      targetBmi
      targetBmiCategory
      basalMetabolicRate
      targetBasalMetabolicRate
      physicalActivityLevelMultiplier
      targetPhysicalActivityLevelMultiplier
      dailyEnergyExpenditure
      targetDailyEnergyExpenditure
    }
  }
`);

const updateClientLatestWeightMutation = graphql(`
  mutation UpdateClientLatestWeight($input: CreateWeightInput!) {
    createWeight(input: $input) {
      errors {
        fullMessage
      }
      dailyWeight {
        id
      }
    }
  }
`);

const updateClientTargetWeightMutation = graphql(`
  mutation UpdateClientTargetWeight($input: UpdateJournalGoalInput!) {
    updateJournalGoal(input: $input) {
      errors {
        fullMessage
      }
      journalGoal {
        id
      }
    }
  }
`);
const updateClientPALMutation = graphql(`
  mutation UpdateClientPAL($input: UpdateLifestyleInput!) {
    updateLifestyle(input: $input) {
      errors {
        fullMessage
      }
      lifestyle {
        id
      }
    }
  }
`);
const updateClientTargetPALMutation = graphql(`
  mutation UpdateClientTargetPAL($input: UpdateJournalGoalInput!) {
    updateJournalGoal(input: $input) {
      errors {
        fullMessage
      }
      journalGoal {
        id
      }
    }
  }
`);

const ClientInformationTable: React.FC = observer(() => {
  const { classes } = useStyles();
  const { clientId } = useParams<{ clientId: string }>();
  const snackbar = useGlobalSnackbar();
  const { data, refetch } = useQuery(clientInformationTableQuery, {
    variables: { clientId: clientId ?? "" },
  });
  const client = data?.client;

  const [weight, weightStr, setWeight] = useNumberInputState({
    min: 0,
    maxDecimalPlace: 2,
  });
  const [targetWeight, targetWeightStr, setTargetWeight] = useNumberInputState({
    min: 0,
    maxDecimalPlace: 2,
  });
  const [pal, setPAL] = useState("");
  const [targetPAL, setTargetPAL] = useState("");

  useUpdateOnChange(client?.latestWeightKg?.value, (latestWeight) => {
    setWeight(latestWeight?.toString() ?? "");
  });
  useUpdateOnChange(client?.journalGoal?.targetWeightKg, (targetWeight) => {
    setTargetWeight(targetWeight?.toString() ?? "");
  });
  useUpdateOnChange(client?.lifestyle?.physicalActivityLevel2, (pal) => {
    setPAL(pal ?? "");
  });
  useUpdateOnChange(client?.journalGoal?.targetPhysicalActivityLevel, (pal) => {
    setTargetPAL(pal ?? "");
  });

  const [updateWeight] = useMutation(updateClientLatestWeightMutation);
  const [updateTargetWeight] = useMutation(updateClientTargetWeightMutation);
  const [updateClientPAL] = useMutation(updateClientPALMutation);
  const [updateClientTargetPAL] = useMutation(updateClientTargetPALMutation);

  useDebouncedEffect(
    () => {
      // Don't update if not changed or empty string
      const initial = client?.latestWeightKg?.value;
      if (weightStr === "" || weight === initial) return;

      updateWeight({
        variables: {
          input: {
            clientId,
            date: formatISODate(new Date()),
            weightKg: weight,
          },
        },
      })
        .then(() => refetch())
        .catch((e) => {
          // reset to initial value when update failed
          setWeight(initial ?? "");
          snackbar.open(displayError(e));
        });
    },
    [weight],
    500
  );

  useDebouncedEffect(
    () => {
      // Don't update if not changed or empty string
      const initial = client?.journalGoal?.targetWeightKg;
      if (targetWeightStr === "" || targetWeight === initial) return;

      updateTargetWeight({
        variables: {
          input: {
            clientId,
            attributes: {
              targetWeightKg: targetWeight,
            },
          },
        },
      })
        .then(() => refetch())
        .catch((e) => {
          // reset to initial value when update failed
          setTargetWeight(initial ?? "");
          snackbar.open(displayError(e));
        });
    },
    [targetWeight],
    500
  );

  const updatePAL = (value: string) => {
    setPAL(value);
    return updateClientPAL({
      variables: {
        input: {
          clientId,
          attributes: {
            physicalActivityLevel2: value as PhysicalActivityLevel2,
          },
        },
      },
    })
      .then(() => refetch())
      .catch((e) => {
        // reset to initial value when update failed
        setPAL(data?.client?.lifestyle?.physicalActivityLevel2 ?? "");
        snackbar.open(displayError(e));
      });
  };
  const updateTargetPAL = (value: string) => {
    setTargetPAL(value);
    return updateClientTargetPAL({
      variables: {
        input: {
          clientId,
          attributes: {
            targetPhysicalActivityLevel: value as TargetPhysicalActivityLevel,
          },
        },
      },
    })
      .then(() => refetch())
      .catch((e) => {
        // reset to initial value when update failed
        setTargetPAL(client?.journalGoal?.targetPhysicalActivityLevel ?? "");
        snackbar.open(displayError(e));
      });
  };

  const store = useContext(MealPlanFormStore.Context);
  const programName = store.programName;
  const age = apply(client?.profile?.ageInYears, (num) => `${num} years`);
  const gender = apply(client?.profile?.gender, humanize);
  const height = apply(client?.latestHeightCm?.value, (value) => `${value} cm`);
  const bmi = apply(client?.bmi, (bmi) => round(bmi, 2));
  const targetBmi = apply(client?.targetBmi, (bmi) => round(bmi, 2));

  return (
    <Grid container spacing={5}>
      <Grid container item xs={12} spacing={1} mb={4}>
        <Grid item xs={2}>
          <Typography variant="caption" color="textSecondary">
            Program
          </Typography>
          <Typography className={classes.bold}>
            {programName ?? <span>&#8212;</span>}
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography variant="caption" color="textSecondary">
            Age
          </Typography>
          <Typography className={classes.bold}>
            {age ?? <span>&#8212;</span>}
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography variant="caption" color="textSecondary">
            Gender
          </Typography>
          <Typography className={classes.bold}>
            {gender ?? <span>&#8212;</span>}
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography variant="caption" color="textSecondary">
            Height
          </Typography>
          <Typography className={classes.bold}>
            {height ?? <span>&#8212;</span>}
          </Typography>
        </Grid>
      </Grid>
      <Grid container item xs={12} className={classes.header} ml={5} pb={5}>
        <Grid item xs={6}>
          {/* Spacing only */}
        </Grid>
        <Grid item xs={3}>
          <Typography variant="caption" color="textSecondary">
            Current
          </Typography>
        </Grid>
        <Grid item xs={3}>
          <Typography variant="caption" color="textSecondary">
            Goal
          </Typography>
        </Grid>
      </Grid>
      <Grid item xs={12} className={classes.sectionHeader}>
        <Typography className={classes.bold}>Client information</Typography>
      </Grid>
      <Grid
        container
        item
        xs={12}
        spacing={2}
        className={classes.row}
        alignItems="center"
      >
        <Grid container item xs={6} alignItems="center">
          <NCIcon icon="weighing-scale" htmlColor={colors.grey} mr="normal" />
          <Typography variant="subtitle2">Weight</Typography>
        </Grid>
        <Grid item xs={3}>
          <TextField
            type="number"
            value={weightStr}
            onChange={(e) => setWeight(e.target.value)}
            size="small"
            margin="dense"
            InputProps={{
              endAdornment: <InputAdornment position="end">kg</InputAdornment>,
            }}
          />
        </Grid>
        <Grid item xs={3}>
          <TextField
            type="number"
            value={targetWeightStr}
            onChange={(e) => setTargetWeight(e.target.value)}
            size="small"
            margin="dense"
            InputProps={{
              endAdornment: <InputAdornment position="end">kg</InputAdornment>,
            }}
          />
        </Grid>
      </Grid>
      <Grid
        container
        item
        xs={12}
        spacing={2}
        className={classes.row}
        alignItems="center"
      >
        <Grid container item xs={6} alignItems="center">
          <NCIcon icon="person" htmlColor={colors.grey} mr="normal" />
          <Typography variant="subtitle2">Body mass index</Typography>
        </Grid>
        <Grid item xs={3}>
          <Typography>
            {bmi ?? <span>&#8212;</span>}{" "}
            <Typography variant="caption" color="textSecondary">
              kg / m&sup2;
            </Typography>
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {client?.bmiCategory ?? <span>&#8212;</span>}
          </Typography>
        </Grid>
        <Grid item xs={3}>
          <Typography>
            {targetBmi ?? <span>&#8212;</span>}{" "}
            <Typography variant="caption" color="textSecondary">
              kg / m&sup2;
            </Typography>
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {client?.targetBmiCategory ?? <span>&#8212;</span>}
          </Typography>
        </Grid>
      </Grid>
      <Grid item xs={12} className={classes.sectionHeader}>
        <Typography className={classes.bold}>Calculations</Typography>
      </Grid>
      <Grid
        container
        item
        xs={12}
        spacing={2}
        className={classes.row}
        alignItems="center"
      >
        <Grid container item xs={6} alignItems="center">
          <NCIcon icon="person-walk" htmlColor={colors.grey} mr="normal" />
          <Typography variant="subtitle2">Physical activity level</Typography>
        </Grid>
        <Grid item xs={3}>
          <Box display="flex" alignItems="center">
            <Box flex={1} marginRight={1}>
              <TextField
                value={pal}
                onChange={(e) => updatePAL(e.target.value)}
                size="small"
                margin="dense"
                select
                fullWidth
              >
                {Object.values(PhysicalActivity).map((value) => (
                  <MenuItem key={value} value={value}>
                    {humanize(value)}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <Typography variant="caption" color="textSecondary">
              (PAL{" "}
              {client?.physicalActivityLevelMultiplier ?? <span>&#8212;</span>})
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={3}>
          <Box display="flex" alignItems="center">
            <Box flex={1} marginRight={1}>
              <TextField
                value={targetPAL}
                onChange={(e) => updateTargetPAL(e.target.value)}
                size="small"
                margin="dense"
                select
                fullWidth
              >
                {Object.values(PhysicalActivity).map((value) => (
                  <MenuItem key={value} value={value}>
                    {humanize(value)}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <Typography variant="caption" color="textSecondary">
              (PAL{" "}
              {client?.targetPhysicalActivityLevelMultiplier ?? (
                <span>&#8212;</span>
              )}
              )
            </Typography>
          </Box>
        </Grid>
      </Grid>
      <Grid
        container
        item
        xs={12}
        spacing={2}
        className={classes.row}
        alignItems="center"
      >
        <Grid container item xs={6} alignItems="center">
          <NCIcon icon="person-bed" htmlColor={colors.grey} mr="normal" />
          <Typography variant="subtitle2">
            Basal metabolic rate (BMR)
          </Typography>
        </Grid>
        <Grid item xs={3}>
          <Typography className={classes.bold}>
            {client?.basalMetabolicRate ?? <span>&#8212;</span>}{" "}
            <Typography variant="caption" color="textSecondary">
              kcal / day
            </Typography>
          </Typography>
        </Grid>
        <Grid item xs={3}>
          <Typography className={classes.bold}>
            {client?.targetBasalMetabolicRate ?? <span>&#8212;</span>}{" "}
            <Typography variant="caption" color="textSecondary">
              kcal / day
            </Typography>
          </Typography>
        </Grid>
      </Grid>
      <Grid
        container
        item
        xs={12}
        spacing={2}
        className={classes.row}
        alignItems="center"
      >
        <Grid container item xs={6} alignItems="center">
          <NCIcon icon="cal-icon" htmlColor={colors.grey} mr="normal" />
          <Typography variant="subtitle2">Daily energy expenditure</Typography>
        </Grid>
        <Grid item xs={3}>
          <Typography className={classes.bold}>
            {client?.dailyEnergyExpenditure ?? <span>&#8212;</span>}{" "}
            <Typography variant="caption" color="textSecondary">
              kcal / day
            </Typography>
          </Typography>
        </Grid>
        <Grid item xs={3}>
          <Typography className={classes.bold}>
            {client?.targetDailyEnergyExpenditure ?? <span>&#8212;</span>}{" "}
            <Typography variant="caption" color="textSecondary">
              kcal / day
            </Typography>
          </Typography>
          <Typography></Typography>
        </Grid>
      </Grid>
    </Grid>
  );
});

export default ClientInformationTable;
